import {AppState} from '@megaphonenpm/mputils/src/AppState.js';
import {
  PRODUCTNAME_SCOREBOARD, STATE_END, STATE_GAME_WINNER, STATE_GAMEOVER,
  STATE_INSTRUCTIONS, STATE_INTERMISSION,
  STATE_INTRODUCTION,
  STATE_MAIN_COUNTDOWN,
  STATE_MAIN_COUNTDOWN_ENDPAUSE, STATE_MAIN_SCOREBOARD,
  STATE_SCENE,
  STATE_SCENE_RESULT,
  STATE_SCENE_SCOREBOARD, STATE_WINNER
} from './constants.js';

import {
  CHANGE,
  COMPLETE
} from '@megaphonenpm/mputils/src/constants.js';
import {get} from 'lodash-es';
import {notNil, isEmpty, notEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {PlaylistService} from './PlaylistService.js';

/**
 * Represents the current state of a MRTS run, including Web Poll-specific state for Web Poll scenes.
 *
 * Web Poll functionality is as follows:
 * 1) Override vote-assignment behavior to allow MRTS-based polls and Web Polls to share a single
 *    property named "votes" for use primarily by views during scenes.
 *
 * 2) Provide a convenience getter method for referencing a Web Poll's poll object, which is
 *    represented as a 'scene' object returned by the 'webpollScene' property.
 *
 * 3) Provide an adapter method to assign a specific web poll's results as the AppState's 'votes'
 *    property.
 *
 */
export class MRTSClientAppState extends AppState {
  constructor (bootManager, sceneService) {
    super();
    this.bootManager = bootManager;

    // The state of the MRTS run
    this.runstate = null;
    this.explicitNextSceneID = '';
    this._scene = null;
    this._webpollScene = null;
    this.votes = [];
    this.overallRankings = [];
    this.sceneRankings = [];
    this.numUsers = 0;
    this.runLifetimeNumUsers = 0;
    this.numAnsweredCorrectly = 0;
    this.numAnsweredIncorrectly = 0;
    this.pointsRemaining = '';
    this.timeRemaining = '';
    this.recentResponses = [];
    // Director's manual override the Playlist or Project "vote visibility" settings
    this.voteVisibility = ''; // Example: { screen: 'visible', controller: 'hidden' }
    this.scoredRun = false;  // Indicates whether a "trivia" scene was reached in the current run
    this.sceneService = sceneService;
    this.webpollState = null;  // E.g., STATE_OPEN or STATE_CLOSED
    // The index of the current scene in the current MRTS run, as reported by MRTS. In some cases,
    // this index is the only means a MRTS client has of determining the current scene index. For
    // example:
    //   -Director connects
    //   -The active playlist changes midway through the current run
    //   -MRTS reloads content in response to an s11 from Director
    //   -Director reconnects
    //   -At this stage, the reconnected Director loads the *new* playlist, but the current scene
    //    is a scene from the previous playlist. Without access to the previous playlist, Director
    //    cannot locate the current scene's index in the previous playlist. MRTS, hence, passes
    //    that index in an UPDATE_STATE_ROUND message.
    this.currentSceneIndexOnServer = null;
    this.fulfillmentCancelled = false;  // Indicates whether MRTS will abort ending the run to
                                        // prevent system failure. See:
                                        // https://docs.google.com/document/d/1QnSiFC_rWWIN_Sbaf86WpWptPVqyVcmVtQRaWM-ffbc
    this.playlistAutoUpdate = true;  // Indicates whether the playlist will automatically be
                                     // updated by MRTS when it changes.
    this.playlistUpdateAvailable = false;  // Assigned when automatic playlist updates have been
                                           // disabled only. When true, either 1) the currently
                                           // activated playlist has been modified in Editor or
                                           // 2) a new playlist has been activated.

    this.on(CHANGE, this.changeListener.bind(this));
    this.bootManager.on(COMPLETE, this.bootCompleteListener.bind(this));
  }

  bootCompleteListener (e) {
    this.state = this.runstate;
  }

  changeListener(e) {
    if (e.detail.prop === 'runstate') {
      if (this.bootManager.complete) {
        this.state = e.detail.value;
      }
    }
  }

  get scene () {
    if (this.isSceneState()) {
      return this._scene;
    } else {
      // There is no scene during non-scene states
      return null;
    }
  }

  /**
   * Assigned externally by MRTSConnection.
   */
  set scene (value) {
    this._scene = value;
  }

  //================================================================================================
  // Web-Poll-SPECIFIC FUNCTIONALITY FOLLOWS
  //================================================================================================

  /**
   * Override the base class's setCompositeProperty() method to prevent the assignment of the
   * 'votes' property during scenes whose product is 'webpoll'. During 'webpoll' scenes, MRTSClient
   * is prevented from assigning to 'votes' so that the current web poll's results can be assigned
   * to 'votes' instead, which allows RunView to use the same code for displaying votes whether those
   * votes are for a MRTS poll or a Web Poll.
   */
  setCompositeProperty (name, newValue, quiet = false) {
    let rejectAssignmentRequest = false;

    if (name === 'votes') {
      if (this.sceneService.isScreenWebPollScene(this.scene)) {
        rejectAssignmentRequest = true;
      }
    }

    if (name === 'sceneRankings') {
      if (this.isCurrentSceneExternalScoreboard()) {
        rejectAssignmentRequest = true;
      }
    }

    if (!rejectAssignmentRequest) {
      super.setCompositeProperty(name, newValue, quiet);
    }
  }

  /**
   * Directly assigns a value to the 'votes' property, bypassing the filter normally applied to
   * assignment attempts in MRTSClientAppState's overriden version of setCompositeProperty() (see
   * additional explanation in the documentation for that method).
   *
   * Used by by WebPollMonitor to update votes during scenes whose product is 'webpoll'.
   */
  setVotesForWebPoll (newValue, quiet = false) {
    super.setCompositeProperty('votes', newValue, quiet);
  }

  /**
   * Directly assigns a value to the 'sceneRankings' property, bypassing the filter normally applied
   * to assignment attempts in AppState's setCompositeProperty(). Used by
   */
  setNonMRTSSceneRankings (newValue, quiet = false) {
    super.setCompositeProperty('sceneRankings', newValue, quiet);
  }

  /**
   * For the current MRTS playlist scene, if the scene's product is 'webpoll', returns the
   * corresponding web poll content, which, itself, is represented as a scene.
   *
   * Background:
   * MRTS runs contain a playlist that contains a sequence of scene objects, each of which represents
   * a unit of content, such as a poll, a quiz, or a graphic title (e.g., a Bulletin).
   *
   * In the Megaphone-platform object model, to maintain interoperability between Web Polls and MRTS
   * runs, and the clients and services that process them, each individual Web Poll is represented as
   * a single-scene playlist, where the single scene is always a poll. Hence, for a MRTS scene whose
   * product is 'webpoll', the current Web Poll's content can be found on the in the DataStore,
   * under pollRecord.poll.scenes[0]. The UpdateWebPollTask class, in combination with
   * WebPollAppStateSynchronizer, keeps the AppState's ._webpollScene property updated for the current scene.
   */
  get webpollScene () {
    if (this.isSceneState()) {
      if (this.sceneService.isScreenWebPollScene(this.scene)) {
        return this._webpollScene;
      } else {
        return null;
      }
    } else {
      // There is no webpoll scene during non-scene states
      return null;
    }
  }

  /**
   * Assigned externally by UpdateWebPollTask.
   */
  set webpollScene (value) {
    this.setCompositeProperty('_webpollScene', value);
  }

  /**
   * Returns the lowercase name of the product this MRTS client should use to render the current scene.
   * In the case of non-webpoll scenes, the effectiveScreenProductName is retrieved from the
   * MRTS scene. In the case of webpoll scenes, the effectiveScreenProductName is retrieved from the
   * webpoll directly (which is always a single-scene playlist, loaded directly from
   * Web Poll Service, not MRTS).
   *
   * Non-webpoll Example:
   *   For a MRTS Pic Pick poll scene, the effectiveScreenProductName would be: picpick
   *
   * Webpoll Example:
   *   For a Face Off Web Poll scene, the effectiveScreenProductName would be: faceoff.
   *   By contrast, the screen product for the same scene in the MRTS playlist would be 'webpoll'.
   */
  get effectiveScreenProductName () {
    let productName = null;

    if (this.sceneService.isScreenWebPollScene(this.scene)) {
      // Web Poll scenes
      productName = this.sceneService.getProductName(this.webpollScene);
    } else {
      // Non-Web Poll scenes
      productName = this.sceneService.getScreenProductName(this.scene);
    }
    if (notNil(productName)) {
      productName = productName.toLowerCase();
    }

    return productName;
  }

  /**
   * Returns the id of the product this MRTS client should use to render the current scene.
   */
  get effectiveScreenProductID () {
    if (this.sceneService.isScreenWebPollScene(this.scene)) {
      return get(this.webpollScene, 'presentation.controller_product.id');
    } else {
      return get(this.scene, 'presentation.screen_product.id');
    }
  }

  /**
   * Returns the "effective scene", which is one of the following:
   * 1) If the MRTS run is on a scene whose screen product is webpoll, then return the nested
   *    Web Poll scene.
   * 2) If the MRTS run is NOT on a scene whose screen product is webpoll, then return the MRTS
   *    scene.
   */
  get effectiveScene () {
    if (this.sceneService.isScreenWebPollScene(this.scene)) {
      return this.webpollScene;
    } else {
      return this.scene;
    }
  }

  isCurrentSceneWebPoll () {
    return this.sceneService.isScreenWebPollScene(this.scene);
  }

  isCurrentSceneExternalScoreboard () {
    return notNil(this.getCurrentSceneSrcID()) && this.sceneService.getScreenProductName(this.scene) === PRODUCTNAME_SCOREBOARD;
  }

  isSceneState (state = this.state) {
    if (state === STATE_SCENE
        || state === STATE_SCENE_RESULT
        || state === STATE_SCENE_SCOREBOARD) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * If the current MRTS scene is an embedded "web poll" (page scene), then getCurrentSceneSrcID()
   * returns the ID of that web poll (page). Otherwise, returns null.
   *
   * @returns A "web poll" (page) ID.
   */
  getCurrentSceneSrcID () {
    return get(this.scene, 'content.src.id');
  }

  /**
   * If the current MRTS scene is an embedded "web poll" (page scene), then getCurrentPageSceneID()
   * returns the ID of the scene within that web poll (page) that was selected for on air display
   * (i.e., the id of the scene/block that was added to playlist in the Pages CMS).
   * Otherwise, getCurrentPageSceneID() returns null.
   *
   * @returns A "web poll" (page) ID.
   */
  getCurrentPageSceneID () {
    return get(this.scene, 'content.src.page_scene_id');
  }

  /**
   * Given the specified dataStore's projectSettings and playlist, returns the name of the MRTS run
   * state that would be expected to follow the specified currentState if the MRTS run were to
   * advance.
   *
   * @param dataStore A DataStore object that that must contain deploymentSettings and playlist properties.
   * @param currentState A state name. If not specified, defaults to the actual current state.
   */
  getNextState (dataStore, currentState = this.state) {
    let projectSettings = dataStore.deploymentSettings;
    let scenes = get(dataStore, 'playlist.scenes', []);
    let numScenes = scenes.length;
    let playlistService = new PlaylistService(dataStore);

    // The list of states in which to search for the next state
    let searchStates = MRTSClientAppState.PRE_SCENE_STATES.slice();
    if (numScenes > 0) {
      searchStates.push(STATE_SCENE);
    }
    if (this.scoredRun) {
      searchStates = searchStates.concat(...MRTSClientAppState.POST_SCENE_STATES);
    } else {
      searchStates = searchStates.concat(...MRTSClientAppState.NON_QUIZ_POST_SCENE_STATES);
    }

    // Search for the next state
    if (this.isSceneState()
        &&
        (playlistService.getSceneIndexForID(get(this.scene, 'id')) < (numScenes-1)
         || notEmpty(this.explicitNextSceneID))) {
        // The current state is a scene, and either this is the last scene or there is an explicit
        // "next scene" assigned, so the next state is also a scene
        return STATE_SCENE;
    } else {
      // Determine where in the list of states to start the search for the next state.
      // For the purposes of the search, treat STATE_SCENE, STATE_SCENE_RESULT, and
      // STATE_SCENE_SCOREBOARD all as STATE_SCENE.
      let searchStartState = this.isSceneState(currentState) ? STATE_SCENE : currentState;
      let currentStateIndex = searchStates.indexOf(searchStartState);
      // If the current state is the last state, start the search for the next state at the
      // beginning of the list of states. Otherwise, start the search right after the current
      // state's index.
      let searchStartIndex = currentStateIndex === searchStates.length-1 ? 0 : searchStates.indexOf(searchStartState)+1;

      // Attempt to find the next state in the state list (starting at searchStartIndex)
      let indexOfNextState = MRTSClientAppState.findIndexOfFirstNonSkippedState(searchStartIndex, searchStates, projectSettings);

      if (indexOfNextState === -1) {
        // The next state wasn't found between the current state's index and the end of the state
        // list, so now search for the next state from the beginning of the state list
        indexOfNextState = MRTSClientAppState.findIndexOfFirstNonSkippedState(0, searchStates, projectSettings);
      }

      // Search done. Return the name of the next state.
      return indexOfNextState === -1 ? null : searchStates[indexOfNextState];
    }
  }
}


MRTSClientAppState.PRE_SCENE_STATES = [STATE_INTRODUCTION, STATE_INSTRUCTIONS, STATE_MAIN_COUNTDOWN, STATE_MAIN_COUNTDOWN_ENDPAUSE];
MRTSClientAppState.NON_QUIZ_POST_SCENE_STATES = [STATE_INTERMISSION];
MRTSClientAppState.POST_SCENE_STATES = [STATE_END, STATE_WINNER, STATE_MAIN_SCOREBOARD, STATE_INTERMISSION];

/**
 * Removes the "state_" prefix from a mrtsclient state-constant value, and returns the result.
 *
 * Example:
 * this.getStateNameFromStateConstant("state_introduction"); // Returns "introduction"
 */
MRTSClientAppState.getStateNameFromStateConstant = (stateConstant) => {
  return isEmpty(stateConstant) ? '' : stateConstant.split('state_')[1];
};

/**
 * Indicates whether the specified state will be skipped in the current run. Specifically,
 * if, in the specified projectSettings, the specified state has a duration greater than zero, then
 * the state will not be skipped and isSkippedState() will return false, otherwise, the state will
 * be skipped, and isSkippedState() will return true.
 *
 * @param state One of the MRTS runstate constants (e.g., STATE_INTRODUCTION).
 * @param projectSettings
 * @returns {boolean}
 */
MRTSClientAppState.isSkippedState = (state, projectSettings) => {
  // Settings for state durations use the MRTS state names, so translate from the state names used
  // by mrtsclient to the corresponding state names used by MRTS.
  state = state === STATE_END ? STATE_GAMEOVER : state;
  state = state === STATE_WINNER ? STATE_GAME_WINNER : state;

  return state === STATE_SCENE ? false : get(projectSettings, `trivia.${MRTSClientAppState.getStateNameFromStateConstant(state)}_duration`) <= 0;
}

/**
 * Starting at fromIndex, returns the first state in searchStates with a non-zero duration.
 *
 * @param fromIndex
 * @param searchStates An array of state names.
 * @param projectSettings A projectSettings object from an application dataStore.
 * @returns {number|*}
 */
MRTSClientAppState.findIndexOfFirstNonSkippedState = (fromIndex, searchStates, projectSettings) => {
  for (let i = fromIndex; i < searchStates.length; i++) {
    if (!MRTSClientAppState.isSkippedState(searchStates[i], projectSettings)) {
      // This state has a duration greater than 0, so return its index
      return i;
    }
  }
  // No states found after the fromIndex with duration greater than 0
  return -1;
}
